import { useFeatureValue } from '@shared/core/featureFlags';
import { createContext } from '@shared/utils/createContext';
import React, { useCallback, useState } from 'react';
import { useShippingAddressTelemetry } from '../ShippingAddressAndCurrencyDialogsProvider/ShippingAddress.telemetry';
import { AddressDialogStep, AddressDialogFields, AddressUsedEnum } from './types';

type AddGiftContext = {
  setStep: (step: AddressDialogStep) => void;
  step: AddressDialogStep;
  onClose: () => void;
  onSaveAddress: (isSuggestedSelected: boolean) => void;
  suggestedAddress: AddressDialogFields | null;
  setSuggestedAddress: (suggestedAddress: AddressDialogFields | null) => void;
  originalAddress: AddressDialogFields | null;
  setOriginalAddress: (originalAddress: AddressDialogFields | null) => void;
  mutationLoading: boolean;
  addressUsed: AddressUsedEnum;
  setAddressUsed: (addressUsed: AddressUsedEnum) => void;
  onDisableAddressSuggestions: () => void;
  onCallSuggestionApi: (isNationalAddress: boolean) => void;
  onSelectSuggestedAddress: (address: AddressDialogFields) => void;
  onGoToNextStep: (address: AddressDialogFields) => void;
  onSaveFormAddress: (address: AddressDialogFields) => void;
};

const [Provider, useAddressDialogContext] = createContext<AddGiftContext>({ name: 'AddGiftContextProvider' });

interface PurchaseConfirmationProviderProps
  extends Readonly<{
    saveAddress: (shippingAddress: AddressDialogFields) => void;
    mutationLoading: boolean;
    onClose: () => void;
    children: React.ReactNode;
    setStep: (step: AddressDialogStep) => void;
    step: AddressDialogStep;
    eventId: string;
  }> {}

const AddressDialogProvider = ({ eventId, onClose, saveAddress, mutationLoading, step, setStep, children }: PurchaseConfirmationProviderProps) => {
  const [originalAddress, setOriginalAddress] = useState<AddressDialogFields | null>(null);
  const [suggestedAddress, setSuggestedAddress] = useState<AddressDialogFields | null>(null);
  const [addressUsed, setAddressUsed] = useState<AddressUsedEnum>(AddressUsedEnum.CUSTOM);

  const telemetry = useShippingAddressTelemetry({ addToContext: { eventId } });
  const enableAddressVerificationModal = useFeatureValue('enableAddressVerificationModal');

  const onDisableAddressSuggestions = useCallback(() => {
    telemetry.deactivateAddressSuggestions();
  }, [telemetry]);

  const onCallSuggestionApi = (isNationalAddress: boolean) => {
    if (isNationalAddress) {
      telemetry.callNationalAddressSuggestionApi();
    } else {
      telemetry.callInternationalAddressSuggestionApi();
    }
  };

  const onSelectSuggestedAddress = (address: AddressDialogFields) => {
    setSuggestedAddress(address);
    telemetry.selectSuggestedAddress();
  };

  const onSaveAddress = (isSuggestedSelected: boolean) => {
    if (isSuggestedSelected && suggestedAddress) {
      saveAddress({ ...suggestedAddress, validated: true });
      telemetry.saveSuggestedAddress();
    } else if (originalAddress) {
      saveAddress({ ...originalAddress, validated: false });
      if (addressUsed === AddressUsedEnum.SUGGESTED_MODIFIED) {
        telemetry.saveSuggestedAddressModified();
      } else {
        telemetry.saveManualAddress();
      }
    }
    setAddressUsed(AddressUsedEnum.CUSTOM);
  };

  const onGoToNextStep = (address: AddressDialogFields) => {
    setOriginalAddress(address);
    if (!enableAddressVerificationModal) {
      return;
    }
    if (addressUsed === AddressUsedEnum.SUGGESTED_ORIGINAL) {
      setAddressUsed(AddressUsedEnum.SUGGESTED_MODIFIED);
      telemetry.recommendAddressModification();
    }
  };

  const onSaveFormAddress = (address: AddressDialogFields) => {
    saveAddress(address);
    if (addressUsed === AddressUsedEnum.SUGGESTED_ORIGINAL) {
      telemetry.saveSuggestedAddress();
    } else if (addressUsed === AddressUsedEnum.CUSTOM) {
      telemetry.saveManualAddress();
    } else {
      telemetry.saveSuggestedAddressModified();
    }
    setAddressUsed(AddressUsedEnum.CUSTOM);
  };

  return (
    <Provider
      value={{
        addressUsed,
        setAddressUsed,
        setStep,
        step,
        mutationLoading,
        originalAddress,
        onCallSuggestionApi,
        onClose,
        onDisableAddressSuggestions,
        onGoToNextStep,
        onSaveAddress,
        onSelectSuggestedAddress,
        onSaveFormAddress,
        suggestedAddress,
        setSuggestedAddress,
        setOriginalAddress
      }}
    >
      {children}
    </Provider>
  );
};

AddressDialogProvider.displayName = 'AddressDialogProvider';

export { AddressDialogProvider, useAddressDialogContext };
